import { ChangeDetectionStrategy, Component, effect, EventEmitter, input, Input, InputSignal, OnInit, Output, output, OutputEmitterRef, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgApexchartsModule } from 'ng-apexcharts';
import { Select, SelectChangeEvent } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Subject, Subscription } from 'rxjs';
import { PDropdownOptionBoolean } from '../../interfaces';
import { SpinnerComponent } from '../spinner/spinner.component';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from "primeng/button";

@Component({
  selector: 'top-ten-bar-chart',
  imports: [NgApexchartsModule, SpinnerComponent, Select, InputText, TranslatePipe, Button],
  templateUrl: './top-ten-bar-chart.component.html',
  styleUrl: './top-ten-bar-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopTenBarChartComponent implements OnInit {
  readonly cssClass = input<string>('');
  readonly chartCase = input<string>('');
  readonly chartCaseParams = input<Record<string, any>>({});
  readonly data: InputSignal<({ title: string, amount: number, codeDesc?: string; } | { category: string; grossAmount: number })[]> = input.required();
  @Input() height: string = '';
  @Input({ required: true }) chartType: string = '';
  @Input() hoverLabels: string = '';
  @Input() isHorizontalBar: boolean = false;
  @Input() graphColors: string = '';
  @Input() isXLabels: boolean = false;
  @Input() xAxisLabel?: string;
  @Input() isYLabels: boolean = false;
  @Input() yAxisLabel?: string;
  @Input() isLegend: boolean = false;
  @Input() format: string = '';
  @Input() chartTitle: string = '';
  @Input() showSearch: boolean = false;
  @Input() brokerSearch!: WritableSignal<string>;
  @Output() brokerSearchChange = new EventEmitter<string>();
  @Input() xAxisRoleTitle: string = '';
  barClick: OutputEmitterRef<string> = output<string>();
  @Input() parentHeightOffset: number = 0;

  currentSearch = '';

  chartReady = input.required<boolean>();

  chartOptions: any = {};

  colors = this.getColors();
  seriesNameX: string = '';
  seriesNameY: string = '';
  designType: string = '';
  horizontal: boolean = false;
  claimHosData: any;
  private langChangeSub?: Subscription;

  kenOptions: PDropdownOptionBoolean[] = [
    { label: 'KEN.TPA_KEN', value: false },
    { label: 'KEN.HOSPITAL_KEN', value: true },
  ];
  investigatorAdjusterOptions = [
  { label: 'BROKER', value: 'BROKER' },
  { label: 'INVESTIGATOR', value: 'INVESTIGATOR' },
  { label: 'ADJUSTER', value: 'ADJUSTER' },
];


  translatedOptions: PDropdownOptionBoolean[] = [];


  translateOptions() {
    const source =
      this.chartTitle === 'PROVIDER_UTILIZATION.TOP10_KEN'
        ? this.kenOptions
        : this.chartTitle === 'DASHBOARD.TOP_10_ROLE'
          ? this.investigatorAdjusterOptions
          : [];

    this.translatedOptions = source.map(option => ({
      ...option,
      label: this.translate.instant(option.label)
    }));
  }



  private destroy$ = new Subject<void>();


  onChange: OutputEmitterRef<boolean | string> = output();


  constructor(private translate: TranslateService
  ) {
    effect(() => {
      if (this.data().length > 0) {
        this.rebuildChart();
      }
    });
  }


  private rebuildChart(): void {
    this.colors = this.getColors(this.graphColors);
    this.handleChartType();
  }


  ngOnInit(): void {
    this.translateOptions();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.translateOptions();
      this.rebuildChart();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }
onBrokerSearch(event: any) {
  this.currentSearch = event.target.value;
}

onBrokerSearchSubmit() {
  this.brokerSearchChange.emit(this.currentSearch);
}


  getColors(color: string = '') {
    if (color == '') {
      return [
        '#4B49AC',
        '#98BDFF',
        '#7DA0FA',
        '#7978E9',
        '#125773',
        '#27CDF2',
        '#488A99',
        '#C6D936',
      ];
    } else return [color];
  }

  handleChartType(): void {
    this.chartOptions =
    {
      series: [
        {
          name: this.hoverLabels,
          data: this.data().map((item: any) => item.amount),
        },
      ],
      chart: {
        width: '100%',
        height: Number(this.height),
        type: 'bar',
        parentHeightOffset: this.parentHeightOffset,
        events: {
          click: (_event: any, _chartContext: any, _opts: any) => {
            this.barClick.emit('');
          },
        },
      },
      plotOptions: {
        bar: {
          distributed: true,
          horizontal: this.isHorizontalBar,
          borderRadius: 5,
        },
      },
      dataLabels: {
        enabled: true,
        position: 'center',
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
        formatter: (val: any) => {
          if (val >= 1000000) {
            return (val / 1000000).toFixed(2) + 'M';
          }
          if (val >= 1000) {
            return (val / 1000).toFixed(2) + 'K';
          }
          return val.toFixed(2);
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: this.getColors(this.graphColors),
      },
      colors: this.getColors(this.graphColors),
      xaxis: {
        categories: this.data().map((item: any) => item.title),
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          show: this.isXLabels,
            style: {
            fontSize: '9px',
            fontWeight: 100,
          }
        },
        title: {
          text: this.chartTitle === 'PROVIDER_UTILIZATION.TOP10_KEN'
            ? this.translate.instant('PROVIDER_UTILIZATION_GRAPH.KEN_CODE')
              : '',
          align: 'center'
        },
      },
      yaxis: {
        labels: {
          show: this.isYLabels,
          formatter: (val: number) => this.formatNumberShort(val)
        },
        title: {
          text:
            this.chartTitle === 'PROVIDER_UTILIZATION.TOP10_KEN'
              ? this.translate.instant('PROVIDER_UTILIZATION_GRAPH.AMOUNT')
              : this.chartTitle === 'DASHBOARD.TOP_10_ROLE'
                ? this.translate.instant('PROVIDER_UTILIZATION_GRAPH.TOTAL_CLAIMS')
                : '',

          align: 'center'
        },
      },
      legend: {
        show: this.isLegend,
        position: 'bottom',
        floating: false,
        fontSize: '14px',
        labels: {
          colors: undefined,
          useSeriesColors: false,
        },
        markers: {
          width: 12,
          height: 12,
        },
        itemMargin: {
          horizontal: 5,
          vertical: 5,
        },
      },
      tooltip: {
        enabled: true,
        intersect: false,
        shared: true,
        followCursor: true,
        custom: (opts: any) => {
          const data = this.data()[opts.dataPointIndex];
          if (!data) return;

          const amount = 'amount' in data ? data.amount : data.grossAmount;
          const title = 'title' in data ? data.title : '';

          const amountLabel = this.translate.instant('PROVIDER_UTILIZATION_GRAPH.AMOUNT')

          const descriptionBlock =
            this.chartTitle === 'PROVIDER_UTILIZATION.TOP10_KEN'
              ? `
          <div style="white-space: normal; max-width: 200px;">
            ${this.translate.instant('PROVIDER_UTILIZATION_GRAPH.DESCRIPTION')}: ${'codeDesc' in data ? data.codeDesc ?? '' : ''}
          </div>
        `
              : '';

          return `
      <div>
        <strong>${title}</strong>
      </div>
      <div>
        ${amountLabel}: ${amount}
      </div>
      ${descriptionBlock}
    `;
        }
      }

    };

  }

  formatNumberShort(n: number): string {
    if (typeof n !== 'number') {
      return n;
    }

    if (n >= 1000000000) return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
    if (n >= 1000000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return n.toString();
  }

  onFilterChange(event: SelectChangeEvent) {

    this.onChange.emit(event.value);
  }

}